import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React from "react";
import { useLocation } from "react-router-dom";

import { cn } from "@/lib/utils";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

type TOutsideInteractionEvent = Parameters<
  NonNullable<
    React.ComponentPropsWithoutRef<
      typeof PopoverPrimitive.Content
    >["onInteractOutside"]
  >
>[0];

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(
  (
    {
      className,
      align = "center",
      sideOffset = 4,
      onPointerDownOutside,
      onInteractOutside,
      children,
      ...props
    },
    ref,
  ) => {
    const location = useLocation();
    const closeButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const contentRef = React.useRef<HTMLDivElement | null>(null);

    const composedContentRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    // NOTE: A click on this popover's own trigger fires as an "outside"
    // pointerdown/interact event too (the trigger isn't inside the portaled
    // content). Radix's own dismiss guard skips closing in that case so the
    // trigger's onClick can toggle it, but that guard runs after our
    // handlers below — so without this check we'd force-close here and the
    // trigger's click would immediately reopen it (NSAI-4139).
    // NOTE: pinned against @radix-ui/react-popover@1.1.15, where
    // PopoverTrigger sets aria-haspopup="dialog" and aria-controls={contentId}.
    // Re-verify this contract on Radix upgrades — a changed attribute value
    // would make this check always return false without a type error or
    // test failure to catch it.
    const isEventFromOwnTrigger = React.useCallback(
      (event: TOutsideInteractionEvent): boolean => {
        const contentId = contentRef.current?.id;
        // NOTE: use Element, not HTMLElement — the trigger's icon (e.g. the
        // chevron) can itself be the event target as an SVGElement, which
        // isn't an HTMLElement but still has closest().
        if (!contentId || !(event.target instanceof Element)) {
          return false;
        }
        const relatedTrigger = event.target.closest('[aria-haspopup="dialog"]');
        return relatedTrigger?.getAttribute("aria-controls") === contentId;
      },
      [],
    );

    const handlePointerDownOutside = React.useCallback<
      NonNullable<
        React.ComponentPropsWithoutRef<
          typeof PopoverPrimitive.Content
        >["onPointerDownOutside"]
      >
    >(
      (event) => {
        onPointerDownOutside?.(event);
        if (!event.defaultPrevented && !isEventFromOwnTrigger(event)) {
          closeButtonRef.current?.click();
        }
      },
      [onPointerDownOutside, isEventFromOwnTrigger],
    );

    const handleInteractOutside = React.useCallback<
      NonNullable<
        React.ComponentPropsWithoutRef<
          typeof PopoverPrimitive.Content
        >["onInteractOutside"]
      >
    >(
      (event) => {
        onInteractOutside?.(event);
        if (!event.defaultPrevented && !isEventFromOwnTrigger(event)) {
          closeButtonRef.current?.click();
        }
      },
      [onInteractOutside, isEventFromOwnTrigger],
    );

    React.useEffect(() => {
      // Close immediately on route changes to avoid lingering popovers during navigation
      closeButtonRef.current?.click();
    }, [location.pathname, location.search, location.hash]);

    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={composedContentRef}
          align={align}
          sideOffset={sideOffset}
          onPointerDownOutside={handlePointerDownOutside}
          onInteractOutside={handleInteractOutside}
          className={cn(
            "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className,
          )}
          {...props}
        >
          {children}
          <PopoverPrimitive.Close
            ref={closeButtonRef}
            aria-hidden="true"
            style={{ display: "none" }}
            tabIndex={-1}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    );
  },
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
